import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { ArrowClockwise, CurrencyDollar, DownloadSimple, Envelope, Printer } from 'phosphor-react';
import { DocumentDelivery, DocumentDeliveryStatus } from '@dill/dill-shared';
import {
  DillButton,
  DillColumn,
  DillSelect,
  DillSummaryCard,
  DillTable,
} from '../../../general/Dill';
import { useAppDispatch } from '../../../../redux/store';
import { getSupplierDocumentDeliveries } from '../../../../redux/services/supplierBuyersService';
import { formatPhoneForDisplay } from '../../../../utils/phone';

type DeliveryRow = Omit<DocumentDelivery, 'pdfStoragePath' | 'idempotencyKey'>;

type TableRow = DeliveryRow & {
  sentDate: Date | null;
  sentMonth: string;
  isBillable: boolean;
  customerName: string;
  documentName: string;
  channelLabel: string;
  destinationLabel: string;
  statusLabel: string;
};

const STATUS_STYLES: Record<DocumentDeliveryStatus, { label: string; className: string }> = {
  QUEUED: { label: 'Queued', className: 'bg-gray-100 text-gray-700' },
  SUBMITTED: { label: 'In progress', className: 'bg-blue-50 text-blue-700' },
  MAILED: { label: 'Mailed', className: 'bg-green-100 text-green-800' },
  DELIVERED: { label: 'Delivered', className: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Failed', className: 'bg-red-50 text-red-700' },
};

// Each successfully mailed invoice or statement is billed to the supplier at this rate. Faxes are
// counted but not priced.
const MAIL_CHARGE_PER_DOCUMENT = 1;
const ALL_MONTHS = 'ALL';

// Only deliveries that actually went out count toward the monthly tally.
const isSent = (delivery: DeliveryRow) =>
  (delivery.channel === 'MAIL' && delivery.status === 'MAILED') ||
  (delivery.channel === 'FAX' && delivery.status === 'DELIVERED');

// A delivery belongs to the month it went out (mailed / fax delivered), falling back to when it
// was submitted or queued for ones that haven't gone out.
const getSentDate = (delivery: DeliveryRow): Date | null => {
  const date =
    (delivery.channel === 'MAIL' ? delivery.mailedAt : delivery.deliveredAt) ||
    delivery.submittedAt ||
    delivery.createdAt;
  return date ? new Date(date) : null;
};

const csvEscape = (value: string | number) => {
  const text = `${value}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const formatCurrency = (amount: number) =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const getDestinationLabel = (delivery: DeliveryRow) => {
  if (delivery.channel === 'FAX') {
    return formatPhoneForDisplay(delivery.faxNumber);
  }
  const address = delivery.mailingAddress;
  if (!address) return '';
  return [address.addressLine1, address.addressLine2, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(', ');
};

function SupplierMailFaxDeliveries({ supplierId }: { supplierId: string }) {
  const dispatch = useAppDispatch();
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));

  const fetchDeliveries = useCallback(async () => {
    if (!supplierId) return;
    setIsLoading(true);
    try {
      const result = await dispatch(getSupplierDocumentDeliveries({ supplierId }));
      const payload = result.payload as { data?: DeliveryRow[] } | null;
      setDeliveries(payload?.data ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const allRows = useMemo<TableRow[]>(
    () =>
      deliveries.map((delivery) => {
        const sentDate = getSentDate(delivery);
        return {
          ...delivery,
          sentDate,
          sentMonth: sentDate ? moment(sentDate).format('YYYY-MM') : '',
          isBillable: isSent(delivery),
          customerName: delivery.buyerName || '',
          documentName:
            delivery.documentLabel ||
            `${delivery.documentType === 'INVOICE' ? 'Invoice' : 'Statement'} ${
              delivery.documentId
            }`,
          channelLabel: delivery.channel === 'FAX' ? 'Fax' : 'Mail',
          destinationLabel: getDestinationLabel(delivery),
          statusLabel: STATUS_STYLES[delivery.status].label,
        };
      }),
    [deliveries]
  );

  const monthOptions = useMemo(() => {
    const months = new Set(allRows.map((row) => row.sentMonth).filter(Boolean));
    months.add(moment().format('YYYY-MM'));
    return [
      ...[...months]
        .sort((a, b) => b.localeCompare(a))
        .map((month) => ({ value: month, label: moment(month, 'YYYY-MM').format('MMMM YYYY') })),
      { value: ALL_MONTHS, label: 'All time' },
    ];
  }, [allRows]);

  const rows = useMemo(
    () =>
      selectedMonth === ALL_MONTHS
        ? allRows
        : allRows.filter((row) => row.sentMonth === selectedMonth),
    [allRows, selectedMonth]
  );

  const tally = useMemo(() => {
    const billable = rows.filter((row) => row.isBillable);
    const count = (channel: 'MAIL' | 'FAX', documentType: 'INVOICE' | 'STATEMENT') =>
      billable.filter((row) => row.channel === channel && row.documentType === documentType).length;
    const mailedInvoices = count('MAIL', 'INVOICE');
    const mailedStatements = count('MAIL', 'STATEMENT');
    return {
      billable,
      mailedInvoices,
      mailedStatements,
      faxedInvoices: count('FAX', 'INVOICE'),
      faxedStatements: count('FAX', 'STATEMENT'),
      mailCharge: (mailedInvoices + mailedStatements) * MAIL_CHARGE_PER_DOCUMENT,
    };
  }, [rows]);

  const periodLabel =
    selectedMonth === ALL_MONTHS ? 'All time' : moment(selectedMonth, 'YYYY-MM').format('MMM YYYY');

  const summaryCards = useMemo<DillSummaryCard[]>(
    () => [
      {
        label: 'Mailed invoices',
        value: `${tally.mailedInvoices}`,
        subLabel: `${formatCurrency(
          tally.mailedInvoices * MAIL_CHARGE_PER_DOCUMENT
        )} at ${formatCurrency(MAIL_CHARGE_PER_DOCUMENT)} each`,
        icon: <Envelope size={16} />,
      },
      {
        label: 'Mailed statements',
        value: `${tally.mailedStatements}`,
        subLabel: `${formatCurrency(
          tally.mailedStatements * MAIL_CHARGE_PER_DOCUMENT
        )} at ${formatCurrency(MAIL_CHARGE_PER_DOCUMENT)} each`,
        icon: <Envelope size={16} />,
      },
      {
        label: 'Mail charge',
        value: formatCurrency(tally.mailCharge),
        subLabel: `${tally.mailedInvoices + tally.mailedStatements} mailed · ${periodLabel}`,
        icon: <CurrencyDollar size={16} />,
        variant: 'success',
      },
      {
        label: 'Faxed',
        value: `${tally.faxedInvoices + tally.faxedStatements}`,
        subLabel: `${tally.faxedInvoices} invoices · ${tally.faxedStatements} statements`,
        icon: <Printer size={16} />,
      },
    ],
    [tally, periodLabel]
  );

  const handleExport = () => {
    const header = [
      'Date sent',
      'Customer',
      'Document type',
      'Document',
      'Channel',
      'Sent to',
      'Charge',
    ];
    const lines = [...tally.billable]
      .sort((a, b) => (a.sentDate?.getTime() ?? 0) - (b.sentDate?.getTime() ?? 0))
      .map((row) => [
        row.sentDate ? moment(row.sentDate).format('YYYY-MM-DD') : '',
        row.customerName,
        row.documentType === 'INVOICE' ? 'Invoice' : 'Statement',
        row.documentName,
        row.channelLabel,
        row.destinationLabel,
        row.channel === 'MAIL' ? MAIL_CHARGE_PER_DOCUMENT.toFixed(2) : '',
      ]);
    const totalRow = ['', '', '', '', '', 'Total mail charge', tally.mailCharge.toFixed(2)];
    const csv = [header, ...lines, totalRow]
      .map((line) => line.map(csvEscape).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `mail-fax-${selectedMonth === ALL_MONTHS ? 'all-time' : selectedMonth}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo<DillColumn<TableRow>[]>(
    () => [
      {
        key: 'sentDate',
        label: 'Date',
        width: 130,
        sortable: true,
        getSortValue: (row) => row.sentDate?.getTime() ?? 0,
        render: (_: any, row) => (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">
              {row.sentDate ? moment(row.sentDate).format('MMM D, YYYY') : '-'}
            </span>
            <span className="text-xs text-gray-500">
              {row.sentDate ? moment(row.sentDate).format('h:mm A') : ''}
            </span>
          </div>
        ),
      },
      {
        key: 'customerName',
        label: 'Customer',
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.customerName,
        render: (_: any, row) => (
          <span className="text-sm text-gray-900 truncate" title={row.customerName}>
            {row.customerName || '-'}
          </span>
        ),
      },
      {
        key: 'documentName',
        label: 'Document',
        width: 200,
        sortable: true,
        getSearchValue: (row) => row.documentName,
        render: (_: any, row) => (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate">{row.documentName}</span>
            <span className="text-xs text-gray-500">
              {row.documentType === 'INVOICE' ? 'Invoice' : 'Statement'}
            </span>
          </div>
        ),
      },
      {
        key: 'channelLabel',
        label: 'Channel',
        width: 110,
        sortable: true,
        filterable: true,
        render: (_: any, row) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
            {row.channel === 'FAX' ? <Printer size={14} /> : <Envelope size={14} />}
            {row.channelLabel}
          </span>
        ),
      },
      {
        key: 'destinationLabel',
        label: 'Sent to',
        width: 260,
        getSearchValue: (row) => row.destinationLabel,
        render: (_: any, row) => (
          <span className="text-sm text-gray-700 truncate" title={row.destinationLabel}>
            {row.destinationLabel || '-'}
          </span>
        ),
      },
      {
        key: 'pageCount',
        label: 'Pages',
        width: 80,
        numeric: true,
        sortable: true,
        render: (value: number | null | undefined) => (
          <span className="text-sm text-gray-700">{value || '-'}</span>
        ),
      },
      {
        key: 'statusLabel',
        label: 'Status',
        width: 220,
        sortable: true,
        filterable: true,
        render: (_: any, row) => {
          const statusStyle = STATUS_STYLES[row.status];
          const detail = row.status === 'FAILED' ? row.lastError : null;
          const trackingLink =
            row.channel === 'MAIL' && row.trackingId
              ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${row.trackingId}`
              : null;
          return (
            <div className="flex flex-col items-start gap-1 min-w-0">
              <span
                className={twMerge(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  statusStyle.className
                )}>
                {statusStyle.label}
              </span>
              {detail && (
                <span className="text-xs text-red-600 truncate max-w-full" title={detail}>
                  {detail}
                </span>
              )}
              {trackingLink && (
                <a
                  href={trackingLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700">
                  Track with USPS
                </a>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-3 w-[200px]">
        <DillSelect value={selectedMonth} onChange={setSelectedMonth} options={monthOptions} />
      </div>
      <div className="flex-1 min-h-0">
        <DillTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.id}
          summaryCards={summaryCards}
          search
          searchPlaceholder="Search customers, documents or destinations"
          sortable
          defaultSort={{ key: 'sentDate', dir: 'desc' }}
          loading={isLoading}
          rowHeight={64}
          striped={false}
          resizable
          showCount={false}
          showActiveFilters
          countLabel="Deliveries"
          emptyMessage={
            selectedMonth === ALL_MONTHS
              ? 'Nothing has been mailed or faxed yet. Deliveries appear here once an invoice or statement is sent by mail or fax.'
              : `Nothing was mailed or faxed in ${moment(selectedMonth, 'YYYY-MM').format(
                  'MMMM YYYY'
                )}.`
          }
          toolbarRight={
            <div className="flex items-center gap-2">
              <DillButton
                label="Export CSV"
                variant="secondary"
                icon={<DownloadSimple size={14} />}
                onClick={handleExport}
                isDisabled={isLoading || tally.billable.length === 0}
                height={31}
              />
              <DillButton
                label="Refresh"
                variant="secondary"
                icon={<ArrowClockwise size={14} />}
                onClick={fetchDeliveries}
                isDisabled={isLoading}
                height={31}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

export default SupplierMailFaxDeliveries;
